import React, { useState, useEffect } from 'react';
import { HelpCircle, Lightbulb, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

interface HelpBubbleProps {
  context: string;
  entityType?: 'lead' | 'contact' | 'company' | 'deal' | 'general';
  entityData?: any;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  trigger?: 'hover' | 'click';
}

interface AIInsight {
  id: string;
  title: string;
  content: string;
  type: 'tip' | 'warning' | 'suggestion' | 'insight';
  confidence: number;
  actionable?: boolean;
  action?: {
    label: string;
    handler: () => void;
  };
}

export function HelpBubble({ 
  context, 
  entityType = 'general', 
  entityData, 
  position = 'top',
  className = '',
  trigger = 'click'
}: HelpBubbleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const entityFingerprint = `${entityData?.id ?? 'new'}:${entityData?.updatedAt ?? entityData?.version ?? ''}`;

  // Fetch AI-powered insights
  const { data: insightsData, isLoading } = useQuery({
    queryKey: ['/api/help/generate-insights', context, entityType, entityFingerprint],
    queryFn: async ({ signal }) => {
      const data = await apiRequest('/api/help/generate-insights', {
        method: 'POST',
        signal,
        isMutation: false,
        notifySuccess: false,
        body: JSON.stringify({
          context,
          entityType,
          entityData
        }),
      });
      if (data === undefined) {
        throw new Error('Failed to fetch insights');
      }
      return data;
    },
    enabled: isOpen, // Only fetch when popover is opened
    staleTime: 5 * 60_000,
  });

  const insights: AIInsight[] = insightsData?.insights || getStaticInsights(context, entityType);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'tip':
        return <Lightbulb className="h-4 w-4 text-yellow-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'suggestion':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'insight':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'tip':
        return 'bg-yellow-50 border-yellow-200';
      case 'warning':
        return 'bg-red-50 border-red-200';
      case 'suggestion':
        return 'bg-green-50 border-green-200';
      case 'insight':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 rounded-full hover:bg-blue-50 ${className}`}
          onMouseEnter={trigger === 'hover' ? () => setIsOpen(true) : undefined}
          onMouseLeave={trigger === 'hover' ? () => setIsOpen(false) : undefined}
        >
          <HelpCircle className="h-4 w-4 text-blue-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        side={position}
        align="start"
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="h-4 w-4 text-blue-500" />
            <h3 className="font-medium text-sm">Suggerimenti AI</h3>
            {isLoading && (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
            )}
          </div>
          
          <div className="space-y-3">
            {insights.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nessun suggerimento disponibile per questo contesto.
              </p>
            ) : (
              insights.map((insight) => (
                <div 
                  key={insight.id}
                  className={`p-3 rounded-lg border ${getInsightColor(insight.type)}`}
                >
                  <div className="flex items-start gap-2">
                    {getInsightIcon(insight.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm">{insight.title}</h4>
                        <Badge 
                          variant="secondary" 
                          className="text-xs"
                        >
                          {Math.round(insight.confidence * 100)}%
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {insight.content}
                      </p>
                      {insight.actionable && insight.action && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 h-7 text-xs"
                          onClick={insight.action.handler}
                        >
                          {insight.action.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getStaticInsights(context: string, entityType: string): AIInsight[] {
  const fallbackInsights: Record<string, AIInsight[]> = {
    'lead-form': [
      {
        id: 'lead-email-validation',
        title: 'Validazione Email',
        content: 'Assicurati che l\'indirizzo email sia corretto e appartenga a un dominio aziendale quando possibile.',
        type: 'tip',
        confidence: 0.9,
        actionable: true
      },
      {
        id: 'lead-source-tracking',
        title: 'Tracciamento Sorgente',
        content: 'Registra sempre la fonte del lead per misurare l\'efficacia del marketing e il ROI.',
        type: 'insight',
        confidence: 0.85,
        actionable: true
      },
      {
        id: 'lead-quick-qualification',
        title: 'Qualificazione Rapida',
        content: 'Qualifica i lead entro 24 ore per massimizzare i tassi di conversione.',
        type: 'suggestion',
        confidence: 0.8,
        actionable: true
      }
    ],
    'contact-form': [
      {
        id: 'contact-complete-profile',
        title: 'Profilo Completo',
        content: 'Profili contatto completi migliorano la costruzione di relazioni e l\'efficacia della comunicazione.',
        type: 'suggestion',
        confidence: 0.85,
        actionable: true
      },
      {
        id: 'contact-social-research',
        title: 'Ricerca Social Media',
        content: 'LinkedIn e altri profili social forniscono contesto prezioso per approcci personalizzati.',
        type: 'tip',
        confidence: 0.8,
        actionable: true
      },
      {
        id: 'contact-communication-prefs',
        title: 'Preferenze di Comunicazione',
        content: 'Documenta i metodi di comunicazione preferiti per rispettare le preferenze del contatto.',
        type: 'insight',
        confidence: 0.75,
        actionable: true
      }
    ],
    'company-form': [
      {
        id: 'company-research',
        title: 'Ricerca Aziendale',
        content: 'Ricerca notizie aziendali, finanziamenti recenti e cambiamenti nella leadership per un migliore contesto.',
        type: 'suggestion',
        confidence: 0.9,
        actionable: true
      },
      {
        id: 'company-decision-makers',
        title: 'Mappatura Decision Maker',
        content: 'Identifica e traccia i principali decision maker e influencer all\'interno dell\'organizzazione.',
        type: 'insight',
        confidence: 0.85,
        actionable: true
      }
    ],
    'deal-form': [
      {
        id: 'deal-realistic-timeline',
        title: 'Timeline Realistiche',
        content: 'Imposta date di chiusura realistiche basate su dimensione e complessità del deal per migliorare l\'accuratezza delle previsioni.',
        type: 'tip',
        confidence: 0.85,
        actionable: true
      },
      {
        id: 'deal-stakeholder-engagement',
        title: 'Coinvolgimento Stakeholder',
        content: 'Assicurati che tutti gli stakeholder chiave siano identificati e coinvolti durante il processo di vendita.',
        type: 'suggestion',
        confidence: 0.8,
        actionable: true
      }
    ]
  };

  return fallbackInsights[context] || [
    {
      id: 'general-data-quality',
      title: 'Qualità dei Dati',
      content: 'Mantieni alta qualità dei dati aggiornando e verificando regolarmente le informazioni.',
      type: 'tip',
      confidence: 0.8,
      actionable: true
    }
  ];
}
